import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';
import { selectSelectedLectures } from '../slices/lectures/lecturesSlice';
import {
  fetchFlashcardCounts,
  type FlashcardCountRequest,
  type FlashcardCountSnapshot,
} from '../services/flashcards';
import type { StudyReturnTo } from '../slices/studyContext/studyContextSlice';
import type { RootState } from '../store/store';
import type { LecturePreview } from '../types/Lecture';

type LectureStudyStartOptions = {
  selectionNounPlural?: string;
  returnTo?: Exclude<StudyReturnTo, null>;
  returnPath?: string | null;
};

type FlashcardModalProps = {
  open: boolean;
  onClose: () => void;
  examName: string;
  lectureIds?: string[];
  returnTo: Exclude<StudyReturnTo, null>;
  returnPath?: string | null;
  startEventName: string;
  onFetchCounts: (
    payload: FlashcardCountRequest
  ) => ReturnType<typeof fetchFlashcardCounts>;
  flashcardCounts?: FlashcardCountSnapshot;
};

export const useLectureFlashcardStudyStart = (
  selectedLecturesOverride?: LecturePreview[],
  options?: LectureStudyStartOptions
) => {
  const {
    selectionNounPlural = 'Lectures',
    returnTo = 'home',
    returnPath = null,
  } = options ?? {};

  const selectedLecturesFromSlice = useSelector((state: RootState) =>
    selectSelectedLectures(state)
  );

  const selectedLectures =
    selectedLecturesOverride ?? selectedLecturesFromSlice;

  const [openStartFlashcardModal, setOpenStartFlashcardModal] = useState(false);

  const [dedupedFlashcardCounts, setDedupedFlashcardCounts] =
    useState<FlashcardCountSnapshot>();

  const [isLoadingFlashcardCounts, setIsLoadingFlashcardCounts] =
    useState(false);

  const lectureIds = useMemo(
    () =>
      Array.from(
        new Set(selectedLectures.map((lecture) => lecture.id).filter(Boolean))
      ),
    [selectedLectures]
  );

  const lectureIdsKey = useMemo(
    () => [...lectureIds].sort().join(','),
    [lectureIds]
  );

  const aggregatedFlashcardCounts = useMemo<FlashcardCountSnapshot>(
    () =>
      selectedLectures.reduce(
        (acc, lecture) => ({
          total: (acc.total ?? 0) + lecture.flashcards.total,
          new: (acc.new ?? 0) + lecture.flashcards.new,
          due: (acc.due ?? 0) + lecture.flashcards.due,
        }),
        { total: 0, new: 0, due: 0 }
      ),
    [selectedLectures]
  );

  const resolvedFlashcardCounts =
    dedupedFlashcardCounts ?? aggregatedFlashcardCounts;

  // The deduped server counts are fetched once per request key. Keying on
  // the selected lectures' own counts (not just their ids) re-runs the fetch
  // when the rows are refreshed, e.g. once a mapping job finalizes: a document
  // selected while its job was still running had cached zero counts, which
  // kept the Flashcards tile hidden until the document was re-selected.
  const flashcardCountsRequestKey = useMemo(
    () =>
      lectureIdsKey
        ? [
            lectureIdsKey,
            aggregatedFlashcardCounts.total,
            aggregatedFlashcardCounts.new,
            aggregatedFlashcardCounts.due,
          ].join('|')
        : '',
    [aggregatedFlashcardCounts, lectureIdsKey]
  );

  const lectureSelectionLabel = useMemo(() => {
    if (selectedLectures.length === 1) {
      return selectedLectures[0].name;
    }
    return `${selectedLectures.length} ${selectionNounPlural} Selected`;
  }, [selectedLectures, selectionNounPlural]);

  useEffect(() => {
    setDedupedFlashcardCounts(undefined);

    if (!flashcardCountsRequestKey) {
      setIsLoadingFlashcardCounts(false);
      return;
    }

    setIsLoadingFlashcardCounts(true);
    let isStale = false;

    const timeoutId = window.setTimeout(() => {
      const loadCounts = async () => {
        try {
          const response = await fetchFlashcardCounts({
            lectureIds: lectureIdsKey.split(','),
          });

          const nextCounts = response?.data ?? response;
          if (!isStale) {
            setDedupedFlashcardCounts(nextCounts);
          }
        } catch (error) {
          console.error('Failed to fetch lecture flashcard counts:', error);
        } finally {
          if (!isStale) {
            setIsLoadingFlashcardCounts(false);
          }
        }
      };

      void loadCounts();
    }, 150);

    return () => {
      isStale = true;
      window.clearTimeout(timeoutId);
    };
  }, [lectureIdsKey, flashcardCountsRequestKey]);

  const openFlashcardModalForLectures = useCallback(() => {
    if (lectureIds.length === 0) {
      enqueueSnackbar('Please select at least one lecture first.', {
        variant: 'warning',
        autoHideDuration: 4000,
      });

      return;
    }

    setOpenStartFlashcardModal(true);
  }, [lectureIds.length]);

  const flashcardModalProps: FlashcardModalProps = useMemo(
    () => ({
      open: openStartFlashcardModal,
      onClose: () => setOpenStartFlashcardModal(false),
      examName: lectureSelectionLabel,
      lectureIds,
      returnTo,
      returnPath,
      startEventName: 'lecture_flashcards_start',
      onFetchCounts: fetchFlashcardCounts,
      flashcardCounts: resolvedFlashcardCounts,
    }),
    [
      lectureIds,
      lectureSelectionLabel,
      openStartFlashcardModal,
      resolvedFlashcardCounts,
      returnTo,
      returnPath,
    ]
  );

  return {
    openFlashcardModalForLectures,
    flashcardModalProps,
    flashcardCounts: resolvedFlashcardCounts,
    isLoadingFlashcardCounts,
  };
};
